import { ClientEvent, Room, SyncState } from 'matrix-js-sdk/src/matrix';
import { useCallback, useEffect, useState } from 'react';

import useMatrixContext from 'contexts/MatrixContext/useMatrixContext';
import useMeetingsScheduleContext from 'contexts/MeetingsScheduleProvider/useMeetingsScheduleContext';
import useEmptyCallWatchdog from 'hooks/useEmptyCallWatchdog/useEmptyCallWatchdog';
import useScheduledMeetingTriggers from 'hooks/useScheduledMeetingTriggers/useScheduledMeetingTriggers';
import { getAllMessengerRooms } from 'modules/Messenger/components/ConversationsList/utils';

interface MeetingTriggersForRoomProps {
  roomId: string;
}

const MeetingTriggersForRoom = ({ roomId }: MeetingTriggersForRoomProps) => {
  useScheduledMeetingTriggers(roomId);
  // BUG-011: chat-side watchdog clears stale MESSENGER_CALL after 30 s of
  // zero RTC participants — fires even when no call-room tab is open.
  useEmptyCallWatchdog(roomId);
  return null;
};

const ScheduledMeetingsWatcher = () => {
  const { matrixClient } = useMatrixContext();
  const { refetchAllMessengerRooms } = useMeetingsScheduleContext();
  const [messengerRoomIds, setMessengerRoomIds] = useState<string[]>([]);

  const refresh = useCallback(() => {
    const ids = getAllMessengerRooms(matrixClient).map((room: Room) => room.roomId);
    setMessengerRoomIds((prev) => {
      if (prev.length === ids.length && prev.every((id, idx) => id === ids[idx])) return prev;
      return ids;
    });
  }, [matrixClient]);

  useEffect(() => {
    refresh();
    // Initial-load + sync hydration: a single batched bulk fetch into the
    // provider store (AC-B6/B15) replaces the per-room HTTP that the per-room
    // hooks used to issue. Per-room merges are driven by useScheduledMeetings.
    refetchAllMessengerRooms();
    const onSync = (state: SyncState) => {
      if (state === SyncState.Syncing) {
        refresh();
        refetchAllMessengerRooms();
      }
    };
    matrixClient.on(ClientEvent.Sync, onSync);
    return () => {
      matrixClient.off(ClientEvent.Sync, onSync);
    };
  }, [matrixClient, refresh, refetchAllMessengerRooms]);

  return (
    <>
      {messengerRoomIds.map((roomId) => (
        <MeetingTriggersForRoom key={roomId} roomId={roomId} />
      ))}
    </>
  );
};

export default ScheduledMeetingsWatcher;
